import React, { useCallback, useRef } from 'react';
import OptionCardSelector from '#/common/components/OptionCardSelector';
import useOcrProcessing, { OcrFieldResult } from '#/common/hooks/useOcrProcessing';

export interface ScanSelectorProps {
  onOcrResult: (file: File | null, result: OcrFieldResult, error?: string) => void;
  onClose: () => void;
  modalChoiceText?: string;
  fieldsToScan: string[];
}

const ScanSelector: React.FC<ScanSelectorProps> = (props) => {
  const { onOcrResult, onClose, modalChoiceText, fieldsToScan } = props;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { processImageFile, loading: ocrLoading } = useOcrProcessing();

  const selectImage = useCallback(
    async (file: File) => {
      try {
        const ocrResults = await processImageFile(file, fieldsToScan);

        if (ocrResults.result) {
          onOcrResult(file, ocrResults.result);
        } else if (ocrResults.error) {
          onOcrResult(null, {}, ocrResults.error);
        }
      } catch (error) {
        onOcrResult(null, {}, error instanceof Error ? error.message : 'Unknown error occurred');
      }
    },
    [processImageFile, onOcrResult, fieldsToScan],
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        await selectImage(file);
        onClose();
      }
    },
    [selectImage, onClose],
  );

  const openFileSelector = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <OptionCardSelector
        onAction={openFileSelector}
        onClose={onClose}
        actionText={modalChoiceText || 'Scan Image'}
        actionIcon="barcode-read"
        loading={ocrLoading}
      />
    </>
  );
};

export default React.memo(ScanSelector);
